<template>
  <v-card class="modern-card" :style="cardStyle">
    <!-- Header moderno con selector de tema -->
    <div class="modern-header" :style="headerStyle">
      <div class="header-content">
        <div class="header-left">
          <v-icon large class="header-icon">mdi-calendar-check</v-icon>
          <div>
            <h2 class="header-title">Aprobar Programación de Vacaciones</h2>
            <p class="header-subtitle">Gestiona y aprueba las solicitudes de vacaciones</p>
          </div>
        </div>
        <div class="header-right">
          <v-menu offset-y style="z-index: 50;">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="theme-selector-btn"
                :style="themeButtonStyle"
                v-bind="attrs"
                v-on="on"
                small
                rounded
              >
                <v-icon left small>mdi-palette</v-icon>
                Tema
              </v-btn>
            </template>
            <v-list class="theme-menu">
              <v-list-item
                v-for="theme in themeOptions"
                :key="theme.name"
                @click="changeTheme(theme)"
                :class="{ 'active-theme': currentTheme.name === theme.name }"
              >
                <v-list-item-avatar>
                  <div 
                    class="theme-color-preview"
                    :style="{ backgroundColor: theme.primary }"
                  ></div>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ theme.displayName }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action v-if="currentTheme.name === theme.name">
                  <v-icon color="success" small>mdi-check</v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
    </div>

    <!-- Barra de búsqueda moderna -->
    <div class="search-section" :style="searchSectionStyle">
      <v-text-field
        v-model="filter.searchText"
        label="Buscar empleado..."
        prepend-inner-icon="mdi-magnify"
        outlined
        dense
        hide-details
        clearable
        class="modern-search"
        :style="searchFieldStyle"
      />
    </div>

    <!-- Filtros modernos -->
    <div style="padding: 0 5px;">
        <v-expansion-panels class="filters-panel">
        <v-expansion-panel>
            <v-expansion-panel-header class="filters-header" :style="filtersHeaderStyle">
            <div class="filters-title">
                <v-icon class="mr-2">mdi-filter-variant</v-icon>
                <span>Filtros Avanzados</span>
            </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="filters-content">
            <v-container>
                <v-row>
                <v-col cols="12" md="4">
                    <div class="filter-group">
                    <label class="filter-label">Configuración de Planilla</label>
                    <s-select-definition 
                        :def="1060" 
                        v-model="filter.FormType" 
                        label="Tipo Planilla" 
                        outlined
                        dense
                        clearable 
                        :disabled="filter.FormTypeAll?true:false"
                        class="modern-select"
                    />
                    <s-select 
                        v-if="!filter.FormTypeAll && filter.FormType" 
                        clearable 
                        label="Año" 
                        :items="years" 
                        outlined 
                        dense
                        v-model="year"
                        class="modern-select"
                    />
                    <s-select 
                        v-if="(filter.FormType == 1 || filter.FormType == 3 || filter.FormType == 4 || filter.FormType == 7 || filter.FormType == 8) && !filter.FormTypeAll" 
                        clearable 
                        label="Mes" 
                        :items="itemsMonth" 
                        outlined 
                        dense
                        v-model="filter.Month"
                        class="modern-select"
                    />
                    <s-periods 
                        v-if="(filter.FormType == 2 || filter.FormType == 5 || filter.FormType == 6) && !filter.FormTypeAll" 
                        clearable 
                        label="Periodo Semana" 
                        v-model="week" 
                        :year="year" 
                        :type="1" 
                        return-object
                        class="modern-select"
                    />
                    </div>
                </v-col>
                <v-col cols="12" md="2">
                    <div class="switch-container">
                    <v-switch 
                        label="Todas las Planillas" 
                        v-model="filter.FormTypeAll" 
                        :true-value="1" 
                        :false-value="0"
                        :color="currentTheme.primary"
                        class="modern-switch"
                    />
                    </div>
                </v-col>
                <v-col cols="12" md="6">
                    <div class="filter-group">
                    <label class="filter-label">Área y Fechas</label>
                    <s-select-area 
                        v-if="$fun.isAdmin()"
                        label="Área"
                        v-model="filter.AreID"
                        outlined
                        dense
                        clearable
                        class="modern-select"
                    />
                    <s-select-area-allow 
                        v-else
                        label="Área"
                        v-model="filter.AreID"
                        outlined
                        dense
                        class="modern-select"
                    />
                    <s-date 
                        label="Fecha Inicio" 
                        v-if="filter.FormTypeAll" 
                        v-model='filter.VctBeginDate' 
                        outlined
                        dense
                        clearable
                        class="modern-date"
                    />
                    <s-date 
                        label="Fecha Fin" 
                        v-if="filter.FormTypeAll" 
                        v-model='filter.VctEndDate' 
                        outlined
                        dense
                        clearable
                        class="modern-date"
                    />
                    </div>
                </v-col>
                </v-row>
            </v-container>
            </v-expansion-panel-content>
        </v-expansion-panel>
        </v-expansion-panels>
    </div>

    <!-- Tabla moderna -->
    <div class="table-container" :style="tableContainerStyle">
      <v-data-table
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="totalItems"
        :loading="loading"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, 100],
          'show-first-last-page': true
        }"
        class="modern-table elevation-3"
        :style="tableStyle"
      >
        <!-- Columna de acciones de aprobación -->
        <template v-slot:item.VctStatusAsign="{ item }">
          <div class="action-buttons">
            <v-btn 
              small 
              rounded
              :color="currentTheme.success"
              @click="VctStatusAsignFuncApproved(item)" 
              class="mr-2 action-btn"
              elevation="2"
              dark
            >
              <v-icon small>far fa-thumbs-up</v-icon>
              <span class="ml-1">Aprobar</span>
            </v-btn>
            <v-btn 
              small 
              rounded
              :color="currentTheme.error"
              @click="VctStatusAsignFuncDisapproved(item)"
              class="action-btn"
              elevation="2"
              dark
            >
              <v-icon small>far fa-thumbs-down</v-icon>
              <span class="ml-1">Rechazar</span>
            </v-btn>
          </div>
        </template>

        <!-- Columna de detalle -->
        <template v-slot:item.VctDetail="{ item }">
          <v-btn 
            small 
            rounded
            :color="currentTheme.info"
            @click="VctDetailFunc(item)"
            class="detail-btn"
            elevation="2"
            dark
          >
            <v-icon small>mdi-eye</v-icon>
            <span class="ml-1">Ver</span>
          </v-btn>
        </template>

        <template v-slot:item.CtrAdmissionDate="{ item }">
            {{$fun.formatDateView(item.CtrAdmissionDate)}}
        </template>

        <template v-slot:item.VctDays="{ item }">
          <div class="d-flex align-center">
            <span class="font-weight-bold">{{ item.VctDays }}d</span>
            <v-tooltip bottom v-if="item.VctAccumulatedDays > 0">
              <template v-slot:activator="{ on }">
                <v-chip v-on="on" x-small color="purple" dark class="ml-1">
                  +{{ item.VctAccumulatedDays }}d
                </v-chip>
              </template>
              <span>Incluye {{ item.VctAccumulatedDays }} días acumulados del periodo {{ item.VctAccumulatedFromPeriod || 'anterior' }}</span>
            </v-tooltip>
          </div>
        </template>

        <!-- Personalización de headers -->
        <!-- <template v-slot:header="{ props }">
          <thead>
            <tr class="modern-header-row" :style="tableHeaderStyle">
              <th 
                v-for="header in props.headers" 
                :key="header.value"
                class="modern-header-cell"
                :style="{ width: header.width + 'px' }"
              >
                {{ header.text }}
              </th>
            </tr>
          </thead>
        </template> -->

        <!-- Loading personalizado -->
        <template v-slot:loading>
          <div class="loading-container">
            <v-progress-circular
              :color="currentTheme.primary"
              indeterminate
              size="50"
            />
            <p class="loading-text">Cargando datos...</p>
          </div>
        </template>

        <!-- Sin datos -->
        <template v-slot:no-data>
          <div class="no-data-container">
            <v-icon size="64" :color="currentTheme.secondary">mdi-calendar-remove</v-icon>
            <h3>No hay solicitudes de vacaciones</h3>
            <p>No se encontraron solicitudes que coincidan con los filtros seleccionados</p>
          </div>
        </template>
      </v-data-table>
    </div>

    <!-- Diálogo de detalle modernizado -->
    <v-dialog v-model="dialogDetail" max-width="1000px" persistent>
      <v-card class="detail-dialog" :style="dialogStyle">
        <div class="dialog-header" :style="dialogHeaderStyle">
          <h3 class="dialog-title">
            <v-icon class="mr-2">mdi-calendar-text</v-icon>
            Detalle de Vacaciones
          </h3>
          <v-btn 
            icon 
            @click="dialogDetail = false"
            class="close-btn"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        
        <v-container class="dialog-content">
          <s-crud
            :config="configVacationDetail"
            title=""
            :filter="filterVacationDetail"
            @save="saveVacationDetail($event)"
            ref="scrudVacationDetail"
            search-input
            class="modern-crud"
            noToolbar
          >
            <template v-slot="props">
              <v-container>
                <v-row>                                        
                  <v-col cols="12" md="4">
                    <s-date 
                      label="Fecha Inicio" 
                      v-model='props.item.VcdDateInit'
                      outlined
                      dense
                      class="modern-date"
                    />
                  </v-col>
                  <v-col cols="12" md="4"> 
                    <s-date 
                      label="Fecha Fin" 
                      v-model='props.item.VcdDateEnd'
                      outlined
                      dense
                      class="modern-date"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <s-text 
                      label="Observación"  
                      v-model="props.item.VcdObservation"
                      outlined
                      dense
                      class="modern-text"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </template>
          </s-crud>
        </v-container>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import ServiceVacations from "../../services/HumanResource/HmnVacationsService";
import sToolbarPerson from "@/components/Utils/Person/sToolbarPerson";
import SSelectPayrollAllow from '../../components/Utils/SSelectPayrollAllow.vue';
import HmnVacationsDetail from '../../services/HumanResource/HmnVacationsDetail';

export default {
  components: {
    sToolbarPerson,
    SSelectPayrollAllow
  },
  data() {
    return {
      // Configuración de temas
      currentTheme: {},
      themeOptions: [
        {
          name: 'blue',
          displayName: 'Azul Corporativo',
          primary: '#1976D2',
          secondary: '#42A5F5',
          accent: '#FFC107',
          success: '#4CAF50',
          warning: '#FF9800',
          error: '#F44336',
          info: '#2196F3'
        },
        {
          name: 'purple',
          displayName: 'Púrpura Moderno',
          primary: '#7B1FA2',
          secondary: '#AB47BC',
          accent: '#E91E63',
          success: '#4CAF50',
          warning: '#FF9800',
          error: '#F44336',
          info: '#9C27B0'
        },
        {
          name: 'green',
          displayName: 'Verde Natural',
          primary: '#388E3C',
          secondary: '#66BB6A',
          accent: '#FF5722',
          success: '#4CAF50',
          warning: '#FF9800',
          error: '#F44336',
          info: '#009688'
        },
        {
          name: 'orange',
          displayName: 'Naranja Energético',
          primary: '#F57C00',
          secondary: '#FFB74D',
          accent: '#E91E63',
          success: '#4CAF50',
          warning: '#FF9800',
          error: '#F44336',
          info: '#FF9800'
        },
        {
          name: 'dark',
          displayName: 'Oscuro Elegante',
          primary: '#424242',
          secondary: '#616161',
          accent: '#00BCD4',
          success: '#4CAF50',
          warning: '#FF9800',
          error: '#F44336',
          info: '#009688'
        },
        {
          name: 'pinkSoft',
          displayName: 'Rosado Pastel',
          primary: '#F48FB1',
          secondary: '#FCE4EC',
          accent: '#EC407A',
          success: '#4CAF50',
          warning: '#FF9800',
          error: '#F44336',
          info: '#F48FB1'
        }
      ],
      filter: {
        AreID: null,
        Month: null,
        Week: null,
        FormType: null,
        FormTypeAll: 1,
        VctBeginDate: null,
        VctEndDate: null,
        Flag: 2,
        isAdmin: this.$fun.isAdmin() ? 1 : 0,
        PstID: this.$fun.getUserInfo().CtrContract?.PstID,
        NtpID: this.$fun.getUserInfo().NtpID,
        searchText: ''
      },
      headers: [
        { text: "ID", value: "VctID" },
        { text: "Acciones", value: "VctStatusAsign", width: 200, sortable: false },
        { text: "Nombre y Apellidos", value: "NtpFullName" },
        { text: "DNI", value: "PrsDocumentNumber" },
        { text: "Tipo de Planilla", value: "TypePayrollName" },
        { text: "Fecha de Ingreso", value: "CtrAdmissionDate", width: 150 },
        { text: "Días de Vacaciones", value: "VctDays", width: 120 },
        { text: "Período", value: "VctPeriod", width: 100 },
        // { text: "Saldo de Vacaciones", value: "VctBalance", width: 120 },
        { text: "Detalle", value: "VctDetail", width: 100, sortable: false },
      ],
      items: [],
      loading: false,
      totalItems: 0,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: []
      },
      headersSearch: [
        { text: "Nombre y Apellidos", value: "NtpFullName" },
        { text: "DNI", value: "PrsDocumentNumber" },
        { text: "Fecha de Ingreso", value: "CtrAdmissionDate" },
        { text: "Período", value: "Periodo" },
        { text: "Días de Vacaciones", value: "VacationDays", width: 90 },
        { text: "Añadir", value: "VctAdd", width: 50, align: "center" },
      ],
      isAdmin: this.$fun.isAdmin(),
      week: null,
      year: null,
      itemPerson: {},
      dialogVacation: false,
      itemsVacation: [],
      textSearch: '',
      dialogDetail: false,
      configVacationDetail: {
        service: HmnVacationsDetail,
        model: {
          VcdID: "ID",
          NtpID: "int",
          NtpFullName: "string",
          PrsDocumentNumber: "string",
          VcdDateInit: "date",
          VcdDateEnd: "date",
        },
        headers: [
          { text: "ID", value: "VcdID" },
          { text: "Nombre y Apellidos", value: "NtpFullName" },
          { text: "DNI", value: "PrsDocumentNumber" },
          { text: "Días de Vacaciones", value: "VcdDays", width: 90 },
          { text: "Fecha Inicio", value: "VcdDateInit", width: 100 },
          { text: "Fecha Fin", value: "VcdDateEnd", width: 100 },
        ],
      },
      filterVacationDetail: {
        VctID: null
      }
    }
  },
  computed: {
    cardStyle() {
      return {
        background: `linear-gradient(135deg, #f5f7fa 0%, ${this.currentTheme.secondary}40 100%)`,
        borderRadius: '16px',
        border: `1px solid ${this.currentTheme.primary}20`,
        overflow: 'hidden'
      };
    },
    headerStyle() {
      return {
        background: `linear-gradient(135deg, ${this.currentTheme.primary} 0%, ${this.currentTheme.secondary} 100%)`,
        color: 'white',
        padding: '24px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      };
    },
    searchSectionStyle() {
      return {
        background: `linear-gradient(135deg, ${this.currentTheme.primary}05 0%, ${this.currentTheme.secondary}05 100%)`,
        padding: '20px 24px',
        borderBottom: `1px solid ${this.currentTheme.primary}10`
      };
    },
    searchFieldStyle() {
      return {
        '--v-theme-primary': this.currentTheme.primary
      };
    },
    filtersHeaderStyle() {
      return {
        background: `linear-gradient(135deg, ${this.currentTheme.primary}10 0%, ${this.currentTheme.secondary}10 100%)`,
        color: this.currentTheme.primary,
        fontWeight: '600'
      };
    },
    tableContainerStyle() {
      return {
        padding: '24px',
        // background: 'white'
      };
    },
    tableStyle() {
      return {
        borderRadius: '12px',
        overflow: 'hidden',
        border: `1px solid ${this.currentTheme.primary}20`
      };
    },
    tableHeaderStyle() {
      return {
        background: `linear-gradient(135deg, ${this.currentTheme.primary}15 0%, ${this.currentTheme.secondary}15 100%)`,
        color: this.currentTheme.primary,
        fontWeight: '600'
      };
    },
    themeButtonStyle() {
      return {
        background: `linear-gradient(135deg, ${this.currentTheme.accent}20 0%, ${this.currentTheme.accent}40 100%)`,
        color: 'white',
        border: 'none',
        backdropFilter: 'blur(10px)'
      };
    },
    dialogStyle() {
      return {
        borderRadius: '16px',
        overflow: 'hidden',
        border: `1px solid ${this.currentTheme.primary}20`
      };
    },
    dialogHeaderStyle() {
      return {
        background: `linear-gradient(135deg, ${this.currentTheme.primary} 0%, ${this.currentTheme.secondary} 100%)`,
        color: 'white',
        padding: '20px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      };
    }
  },
  methods: {
    // Gestión de temas
    changeTheme(theme) {
      this.currentTheme = { ...theme };
      localStorage.setItem('vacationApprovalTheme', JSON.stringify(theme));
      this.$forceUpdate();
    },
    loadSavedTheme() {
      const savedTheme = localStorage.getItem('vacationApprovalTheme');
      if (savedTheme) {
        this.currentTheme = JSON.parse(savedTheme);
      } else {
        this.currentTheme = { ...this.themeOptions[0] }; // Tema por defecto
      }
    },
    
    // Métodos de datos existentes
    async loadData() {
      this.loading = true;
      
      const paginationParams = {
        filter: this.filter,
        draw: 1,
        start: (this.options.page - 1) * this.options.itemsPerPage,
        length: this.options.itemsPerPage,
        order: [],
        searchText: this.filter.searchText
      };

      if (this.options.sortBy.length > 0) {
        paginationParams.order = [{
          column: this.options.sortBy[0],
          dir: this.options.sortDesc[0] ? 'desc' : 'asc'
        }];
      }

      try {
        const response = await ServiceVacations.pagination(paginationParams, this.$fun.getUserID());
        if (response.status === 200) {
          this.items = response.data.data;
          this.totalItems = response.data.recordsTotal;
        }
      } catch (error) {
        console.error("Error al cargar datos:", error);
        this.$fun.alert("Error al cargar los datos", "error");
      } finally {
        this.loading = false;
      }
    },

    returnPerson(value, item) {
      if (value == null) {
        return;
      } else {
        this.itemPerson = { ...value };
        if (value.CtrAdmissionDate) {
          const currentDate = new Date();
          const CtrAdmissionDate = new Date(value.CtrAdmissionDate);
          const timeDiff = currentDate - CtrAdmissionDate;
          const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

          this.itemPerson.WorkingDays = daysDiff;

          if (daysDiff > 365) {
            console.log(`El trabajador ${value.NtpFullName} tiene ${daysDiff} días laborados.`);
            item.NtpFullName = value.NtpFullName;
            item.PrsDocumentNumber = value.PrsDocumentNumber;
            item.NtpID = value.NtpID;
          } else {
            this.$fun.alert(`El trabajador ${value.NtpFullName} tiene ${daysDiff} días laborados.`, "warning");
          }
        } else {
          this.$fun.alert(`El trabajador ${value.NtpFullName} tiene 0 días laborados.`, "warning");
          item.NtpFullName = null;
          item.NtpID = null;
        }
      }
    },

    save(item) {
      const usrAdmin = this.$fun.isAdmin();
      
      if (!item.NtpID) {
        item.PrsDocumentNumber = this.$fun.getUserInfo().PrsDocumentNumber;
        this.isAdmin = true;
        this.$refs.toolbarPerson.returnPerson({
          PrsDocumentNumber: item.PrsDocumentNumber
        });
      }

      setTimeout(() => {
        this.isAdmin = usrAdmin;

        if (item.PrsDocumentNumber.length <= 0) {
          this.$fun.alert("El campo Documento es Obligatorio", "warning");
          return;
        }

        if (!item.VctDateEnd || !item.VctDateInit) {
          this.$fun.alert("Las fechas son obligatorias", "warning");
          return;
        }

        item.save();
      }, 0);
    },

    selection() {
      if (this.filter.Month != null) {
        let month = this.filter.Month.toString().padStart(2, "0");
        let year = this.year;

        this.filter.VctBeginDate = this.$moment(`${year}-${month}-01`).format("YYYY-MM-DD");
        this.filter.VctEndDate = this.$moment(`${year}-${month}-01`).endOf("month").format("YYYY-MM-DD");
      }
    },

    VctStatusAsignFuncApproved(item) {
      item.VctApproved = 2;
      item.UsrCreateID = this.$fun.getUserID();

      this.$fun.alertFull("¿Seguro de aprobar?", "question")
        .then(r => {
          if (r.value) {
            ServiceVacations.save(item, this.$fun.getUserID()).then((res) => {
              if (res.status == 200) {
                this.$fun.alert("Vacaciones Aprobadas", "success");
                this.loadData();
              }
            })
          }
        });
    },

    VctStatusAsignFuncDisapproved(item) {
      item.VctApproved = 3;
      item.UsrCreateID = this.$fun.getUserID();

      this.$fun.alertFull("¿Seguro de desaprobar?", "question")
        .then(r => {
          if (r.value) {
            ServiceVacations.save(item, this.$fun.getUserID()).then((res) => {
              if (res.status == 200) {
                this.$fun.alert("Vacaciones Desaprobadas", "success");
                this.loadData();
              }
            })
          }
        });
    },

    VctDetailFunc(row) {
      this.filterVacationDetail.VctID = row.VctID;
      this.dialogDetail = true;
    },

    saveVacationDetail(event) {
      // Implementar lógica de guardado del detalle
      console.log('Guardando detalle:', event);
    }
  },
  watch: {
    filter: {
      handler() {
        this.loadData();
      },
      deep: true
    },
    options: {
      handler() {
        this.loadData();
      },
      deep: true
    },
    'filter.Month'() {
      this.selection();
    },
    year() {
      this.week = [];
    },
    week() {
      this.filter.VctBeginDate = this.week.PdsDateBegin;
      this.filter.VctEndDate = this.week.PdsDateEnd;
    },
    'filter.FormTypeAll'() {
      this.filter.AreID = null;
      this.filter.FormType = null;
      this.filter.VctBeginDate = null;
      this.filter.VctEndDate = null;
      this.week = [];
    }
  },
  created() {
    this.loadSavedTheme();
    this.itemsMonth = this.$const.Months;
    this.years = this.$fun.getTop5LastYears();
    this.loadData();
  }
}
</script>

<style scoped>
::v-deep(.v-text-field--outlined.v-input--dense .v-label){
    top: 5px;
}

.modern-card {
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
}

.modern-header {
  position: relative;
  overflow: hidden;
}

.modern-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100"><path d="M0 0v100h1000V0z" fill="white" opacity="0.05"/><path d="M0 50c250 0 250-50 500-50s250 50 500 50v50H0z" fill="white" opacity="0.1"/></svg>');
  background-size: cover;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 1;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 8px;
  backdrop-filter: blur(10px);
}

.header-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.header-subtitle {
  margin: 4px 0 0 0;
  opacity: 0.9;
  font-size: 0.9rem;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.theme-selector-btn {
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  transition: all 0.3s ease;
}

.theme-selector-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.theme-menu {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
}

.theme-color-preview {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.active-theme {
  background: linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%);
  color: white;
}

.search-section {
  backdrop-filter: blur(10px);
}

.modern-search {
  max-width: 400px;
  margin: 0 auto;
}

.modern-search >>> .v-input__control {
  border-radius: 25px;
}

.modern-search >>> .v-text-field__details {
  display: none;
}

.filters-panel {
  margin: 10px 0;
  max-width: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.filters-header {
  font-weight: 600;
  border-radius: 12px 12px 0 0;
}

.filters-title {
  display: flex;
  align-items: center;
  font-size: 1.1rem;
}

.filters-content {
  background: #fafafa;
  border-top: 1px solid #e0e0e0;
}

.filter-group {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.filter-label {
  display: block;
  font-weight: 600;
  color: #555;
  margin-bottom: 12px;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.modern-select, .modern-date, .modern-text {
  margin-bottom: 16px;
}

.modern-select >>> .v-input__control,
.modern-date >>> .v-input__control,
.modern-text >>> .v-input__control {
  border-radius: 8px;
}

.switch-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.modern-switch {
  margin: 0;
}

.table-container {
  border-radius: 0 0 16px 16px;
}

.modern-table {
  border-radius: 12px;
  overflow: hidden;
}

.modern-table >>> .v-data-table__wrapper {
  border-radius: 12px;
}

.modern-header-row {
  background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
}

.modern-header-cell {
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.85rem;
  padding: 16px 12px;
  border-bottom: 2px solid #ddd;
}

.action-buttons {
  display: flex;
  gap: 2px;
  /* flex-wrap: wrap; */
}

.action-btn {
  min-width: 100px;
  font-weight: 500;
  text-transform: none;
  transition: all 0.3s ease;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.detail-btn {
  min-width: 80px;
  font-weight: 500;
  text-transform: none;
  transition: all 0.3s ease;
}

.detail-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px;
  gap: 16px;
}

.loading-text {
  font-size: 1.1rem;
  color: #666;
  margin: 0;
}

.no-data-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px;
  gap: 16px;
  color: #666;
}

.no-data-container h3 {
  margin: 0;
  font-size: 1.3rem;
  color: #555;
}

.no-data-container p {
  margin: 0;
  font-size: 1rem;
  text-align: center;
  max-width: 300px;
}

.detail-dialog {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.2);
}

.dialog-header {
  position: relative;
  overflow: hidden;
}

.dialog-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100"><path d="M0 0v100h1000V0z" fill="white" opacity="0.05"/></svg>');
  background-size: cover;
}

.dialog-title {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  position: relative;
  z-index: 1;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.dialog-content {
  /* padding: 24px; */
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
}

.modern-crud {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

/* Animaciones */
.modern-card {
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.action-btn, .detail-btn {
  animation: fadeIn 0.8s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
  
  .header-left {
    justify-content: center;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 4px;
  }
  
  .action-btn {
    min-width: 60px;
    font-size: 0.8rem;
  }
  
  .modern-search {
    max-width: 100%;
  }
  
  .filter-group {
    padding: 12px;
  }
}

/* Mejoras adicionales para elementos específicos */
.modern-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.modern-table >>> .v-data-table__wrapper tbody tr {
  transition: all 0.3s ease;
}

.modern-table >>> .v-data-table__wrapper tbody tr:hover {
  background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.modern-table >>> .v-data-footer {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 0 0 12px 12px;
}

/* Estilos para los componentes personalizados */
.modern-select >>> .v-select__selection,
.modern-date >>> .v-text-field__slot,
.modern-text >>> .v-text-field__slot {
  color: #333;
}

.modern-select >>> .v-input__icon,
.modern-date >>> .v-input__icon,
.modern-text >>> .v-input__icon {
  color: #666;
}

.modern-select >>> .v-text-field__details,
.modern-date >>> .v-text-field__details,
.modern-text >>> .v-text-field__details {
  margin-top: 8px;
}

/* Efectos de hover mejorados */
.filter-group:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
  transition: all 0.3s ease;
}

.theme-menu .v-list-item:hover {
  background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
}

.theme-menu .v-list-item {
  transition: all 0.3s ease;
}

/* Mejoras en la accesibilidad */
.action-btn:focus,
.detail-btn:focus,
.theme-selector-btn:focus {
  outline: 2px solid #2196F3;
  outline-offset: 2px;
}

.modern-search >>> .v-text-field__slot input:focus {
  outline: none;
}

/* Estados de carga mejorados */
.modern-table >>> .v-data-table__progress {
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}
</style>